import { Check } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { ParticleOrb } from "../shared/ParticleOrb";

const ASSISTANT_ACCENTS: Record<string, { accent: string; lightAccent: string }> = {
  Merlin: { accent: "#4A90D9", lightAccent: "#1E4FD8" },
  Mona: { accent: "#38BDF8", lightAccent: "#0284C7" },
};

export function AssistantActivity({
  name,
  status,
  prompt,
  steps,
  trailing,
  accentCls,
}: {
  name: string;
  status: string;
  prompt: string;
  steps: string[];
  trailing: string;
  accentCls: string;
}) {
  const reduced = useReducedMotion();
  const container = { hidden: {}, show: { transition: { staggerChildren: reduced ? 0 : 0.35, delayChildren: 0.2 } } };
  const item = {
    hidden: reduced ? { opacity: 0 } : { opacity: 0, x: -8 },
    show: { opacity: 1, x: 0, transition: { duration: 0.35 } },
  };

  return (
    <div className="rounded-2xl border border-[#0A0A0F]/10 dark:border-white/10 bg-white dark:bg-[#0D0D10] overflow-hidden">
      <div className="px-5 py-3.5 border-b border-[#0A0A0F]/10 dark:border-white/10 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-[#0A0A0F] dark:text-white">User</span>
      </div>
      <div className="px-5 py-4 border-b border-[#0A0A0F]/10 dark:border-white/10">
        <p className="text-sm text-[#0A0A0F] dark:text-[#E5E5E8]">{prompt}</p>
      </div>
      <div className="px-5 py-3.5 border-b border-[#0A0A0F]/10 dark:border-white/10 flex items-center gap-2.5">
        <span className="w-6 h-6 shrink-0 rounded-full overflow-hidden">
          <ParticleOrb variant="ai-brain" accent={ASSISTANT_ACCENTS[name]?.accent} lightAccent={ASSISTANT_ACCENTS[name]?.lightAccent} />
        </span>
        <span className={`text-[11px] font-semibold uppercase tracking-wide ${accentCls}`}>
          {name} · {status}
        </span>
      </div>
      <motion.div
        className="px-5 py-4 space-y-2"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-40px" }}
        variants={container}
      >
        {steps.map((step) => (
          <motion.div key={step} variants={item} className="flex items-start gap-2.5">
            <Check size={13} className={`mt-0.5 shrink-0 ${accentCls}`} />
            <span className="text-sm text-[#5C5C66] dark:text-[#9A9A9A]">{step}</span>
          </motion.div>
        ))}
        <motion.p variants={item} className="text-sm italic text-[#0A0A0F] dark:text-white pt-1">{trailing}</motion.p>
      </motion.div>
    </div>
  );
}
